import React from 'react';

import Icon from 'views/site/Icon.js';

import Button from 'views/site/Button.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Hero
 *
 * Ported from the storefront's product-led hero and re-aimed at a dealership.
 * The storefront sells water sensors to anyone in a serviceable area; a
 * replicated website sells a whole installed network on behalf of one local
 * company, so the headline is the network's actual claim — the Event
 * Positioning System — rather than a leak.
 *
 * Nothing here fabricates social proof. The storefront hero carries a hard
 * coded star rating, which would be a made-up number about a real, named
 * company on this site. The trust line states only what is true of every
 * dealership; real ratings arrive with the Google reviews section.
 */

const Hero = ({ dealership, onOpenQuiz, onRequestQuote, showProducts = true }) => {

    const mobile = Utils.isMobile();

    const onHowItWorksClick = () => {
        Utils.scrollToElement('how-it-works');
    }

    const getEyebrow = () => {
        let locality = dealership.locality && dealership.administrative_area_level_1
            ? `${dealership.locality}, ${dealership.administrative_area_level_1}`
            : null;
        return (
            <span
            key={'eyebrow'}
            style={{
                ...Theme.textStyles.label(),
                display: 'block',
                marginBottom: 16
            }}>{locality ? `Protecting homes in ${locality}` : 'The Home Safe Network'}</span>
        )
    }

    const getHeading = () => {
        return (
            <span
            key={'heading'}
            style={{
                ...Theme.textStyles.heroHeading(),
                display: 'block',
                margin: '0 auto 22px',
                maxWidth: '17ch',
                textWrap: 'balance'
            }}>{'Danger in one room. Warning in every room.'}</span>
        )
    }

    const getImage = () => {
        return (
            <div
            key={'image'}
            style={{
                alignItems: 'center',
                backgroundColor: Theme.colors.offWhite,
                borderRadius: 28,
                display: 'flex',
                height: mobile ? 300 : 400,
                justifyContent: 'center',
                marginBottom: mobile ? 32 : 64,
                marginTop: mobile ? 8 : 64,
                overflow: 'hidden'
            }}>
                <img
                alt={'The connected sensor network'}
                src={'images/network-sensors-collection-white.jpg'}
                style={{
                    height: '100%',
                    objectFit: 'cover',
                    width: '100%'
                }} />
            </div>
        )
    }

    const getBody = () => {
        return (
            <div key={'body'}>
                <span style={{
                    ...Theme.textStyles.body({ fontSize: mobile ? 17 : 21, lineHeight: 1.45 }),
                    display: 'block',
                    margin: '0 auto',
                    maxWidth: '42ch'
                }}>{`Smoke, heat, carbon monoxide and water sensors that talk to each other, and to your phone. Professionally installed by ${dealership.name}, with no monitoring fees.`}</span>

                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 26,
                    justifyContent: 'center',
                    marginTop: 30
                }}>
                    <Button
                    label={"What Your Detectors Don't Catch"}
                    onClick={onOpenQuiz || onRequestQuote}
                    type={'primary'} />
                    <span
                    onClick={onHowItWorksClick}
                    style={{
                        color: Theme.colors.primary(),
                        cursor: 'pointer',
                        fontFamily: Theme.fonts.body,
                        fontSize: 17,
                        fontWeight: 500
                    }}>{'See how it works ›'}</span>
                </div>

                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: mobile ? 'column' : 'row',
                    gap: mobile ? 8 : 18,
                    justifyContent: 'center',
                    marginTop: 26
                }}>
                    {['Locally owned and operated', 'Professionally installed', 'No monitoring fees'].map((item, index) => (
                        <span
                        key={index}
                        style={{
                            ...Theme.textStyles.caption({ color: Theme.colors.subText() }),
                            alignItems: 'center',
                            display: 'inline-flex',
                            gap: 7
                        }}>
                            <Icon name={'check-lg'} size={11} color={Theme.colors.primary()} />
                            {item}
                        </span>
                    ))}
                </div>
            </div>
        )
    }

    // Mobile puts the image under the headline so the visual is not pinned to
    // the top or bottom of the fold; desktop runs the whole text block first.
    //
    // The photograph is of the devices, so it goes when the products are
    // hidden (see Catalog.isVisible). On desktop it was also what put space
    // under the buttons, so the section pads its own bottom instead.
    let children = mobile
        ? [getEyebrow(), getHeading(), showProducts && getImage(), getBody()]
        : [getEyebrow(), getHeading(), getBody(), showProducts && getImage()];

    return (
        <section
        id={'hero'}
        style={{
            backgroundColor: Theme.colors.white,
            paddingTop: Theme.layout.headerOffset(),
            width: '100%'
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                margin: '0 auto',
                maxWidth: 1250,
                padding: mobile ? '40px 20px 44px' : `72px 40px ${showProducts ? 0 : 88}px`,
                textAlign: 'center'
            }}>
                {children}
            </div>
        </section>
    )
}

export default Hero;
